/**
 * Ziehen und Ablegen für Listen, mit Maus, Finger und Tastatur.
 *
 * Gezogen wird an einem Griff. Während des Ziehens ordnet sich die Liste
 * sofort neu, so dass man sieht, wo der Eintrag landet. Einträge können auch
 * in eine andere Gruppe derselben Liste wandern (zum Beispiel eine andere
 * Warengruppe). Am Bildschirmrand rollt die Seite mit. Mit den Pfeiltasten
 * auf dem Griff lässt sich ein Eintrag ebenfalls verschieben.
 *
 * Erkannt werden die Einträge über data-Attribute; die Seite muss dafür nur
 * itemProps und – für leere Gruppen – containerProps an die Elemente geben.
 */
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { KeyboardEvent, PointerEvent } from 'react';

/** Einträge je Gruppe in Anzeigereihenfolge. */
export type DragGroups = Readonly<Record<string, readonly string[]>>;

/** Ergebnis nach dem Ablegen. */
export interface DropResult {
  readonly id: string;
  /** Gruppe, in der der Eintrag jetzt liegt. */
  readonly group: string;
  /** Gruppe, aus der er kam. */
  readonly fromGroup: string;
  /** Stelle innerhalb der Gruppe. */
  readonly index: number;
  /** Alle Gruppen nach dem Verschieben. */
  readonly groups: Readonly<Record<string, string[]>>;
}

/** Abstand zum Bildschirmrand, ab dem die Seite mitrollt. */
const EDGE = 72;
/** Höchstes Tempo beim Mitrollen in Pixeln je Bild. */
const SCROLL_SPEED = 14;

/** Sucht die Gruppe und Stelle eines Eintrags. */
function locate(groups: DragGroups, id: string): { group: string; index: number } | null {
  for (const [group, ids] of Object.entries(groups)) {
    const index = ids.indexOf(id);
    if (index !== -1) {
      return { group, index };
    }
  }
  return null;
}

/** Verschiebt einen Eintrag an eine Stelle; liefert neue Gruppen. */
export function moveInGroups(
  groups: DragGroups,
  id: string,
  group: string,
  index: number,
): Record<string, string[]> {
  const next: Record<string, string[]> = {};
  for (const [key, ids] of Object.entries(groups)) {
    next[key] = ids.filter((entry) => entry !== id);
  }
  const target = next[group] ?? [];
  const position = Math.max(0, Math.min(target.length, index));
  next[group] = [...target.slice(0, position), id, ...target.slice(position)];
  return next;
}

/** Gleiche Reihenfolge in allen Gruppen? */
function sameGroups(a: DragGroups, b: DragGroups): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const key of keys) {
    const left = a[key] ?? [];
    const right = b[key] ?? [];
    if (left.length !== right.length || left.some((entry, index) => entry !== right[index])) {
      return false;
    }
  }
  return true;
}

/**
 * Ziehen und Ablegen für eine Liste aus einer oder mehreren Gruppen.
 * @param groups Aktuelle Reihenfolge je Gruppe.
 * @param onDrop Wird nach einer Änderung aufgerufen, nicht beim Ablegen am alten Platz.
 */
export function useDragReorder(groups: DragGroups, onDrop: (result: DropResult) => void) {
  const scope = useId();
  const [preview, setPreview] = useState<Record<string, string[]> | null>(null);
  const [dragging, setDragging] = useState<string | null>(null);

  // Werte für die Ereignisse, ohne bei jedem Bild neue Funktionen zu bauen.
  const previewRef = useRef<Record<string, string[]> | null>(null);
  const startRef = useRef<DragGroups>(groups);
  const draggingRef = useRef<string | null>(null);
  const pointerRef = useRef<{ x: number; y: number } | null>(null);
  const frameRef = useRef<number | null>(null);
  /** Meldet die Ereignisse am Fenster wieder ab. */
  const releaseRef = useRef<(() => void) | null>(null);
  const onDropRef = useRef(onDrop);
  onDropRef.current = onDrop;

  const update = useCallback((next: Record<string, string[]> | null) => {
    previewRef.current = next;
    setPreview(next);
  }, []);

  /** Ermittelt die Stelle unter dem Zeiger und ordnet die Vorschau neu. */
  const hitTest = useCallback(() => {
    const id = draggingRef.current;
    const pointer = pointerRef.current;
    const current = previewRef.current;
    if (id === null || pointer === null || current === null) {
      return;
    }
    const element = document.elementFromPoint(pointer.x, pointer.y);
    const item = element?.closest<HTMLElement>('[data-sort-item]');
    let next: Record<string, string[]> | null = null;
    if (item !== null && item !== undefined && item.dataset.sortScope === scope) {
      const targetId = item.dataset.sortItem ?? '';
      const group = item.dataset.sortGroup ?? '';
      if (targetId === id) {
        return;
      }
      const rect = item.getBoundingClientRect();
      const before = pointer.y < rect.top + rect.height / 2;
      const without = (current[group] ?? []).filter((entry) => entry !== id);
      const position = without.indexOf(targetId);
      if (position === -1) {
        return;
      }
      next = moveInGroups(current, id, group, before ? position : position + 1);
    } else {
      // Über einer leeren Gruppe: dort ablegen.
      const container = element?.closest<HTMLElement>('[data-sort-container]');
      if (container === null || container === undefined || container.dataset.sortScope !== scope) {
        return;
      }
      const group = container.dataset.sortContainer ?? '';
      if ((current[group] ?? []).some((entry) => entry !== id)) {
        return;
      }
      next = moveInGroups(current, id, group, 0);
    }
    if (!sameGroups(next, current)) {
      update(next);
    }
  }, [scope, update]);

  /** Rollt die Seite am Rand mit und prüft danach die Stelle neu. */
  const autoScroll = useCallback(() => {
    const pointer = pointerRef.current;
    if (draggingRef.current === null) {
      frameRef.current = null;
      return;
    }
    if (pointer !== null) {
      const top = EDGE - pointer.y;
      const bottom = pointer.y - (window.innerHeight - EDGE);
      const speed = top > 0 ? -top : bottom > 0 ? bottom : 0;
      if (speed !== 0) {
        window.scrollBy(0, Math.max(-SCROLL_SPEED, Math.min(SCROLL_SPEED, speed / 4)));
        hitTest();
      }
    }
    frameRef.current = window.requestAnimationFrame(autoScroll);
  }, [hitTest]);

  /** Beendet das Ziehen; bei change wird das Ergebnis gemeldet. */
  const finish = useCallback(
    (commit: boolean) => {
      const id = draggingRef.current;
      const result = previewRef.current;
      draggingRef.current = null;
      pointerRef.current = null;
      releaseRef.current?.();
      releaseRef.current = null;
      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
      setDragging(null);
      update(null);
      if (!commit || id === null || result === null || sameGroups(result, startRef.current)) {
        return;
      }
      const to = locate(result, id);
      const from = locate(startRef.current, id);
      if (to !== null && from !== null) {
        onDropRef.current({ id, group: to.group, fromGroup: from.group, index: to.index, groups: result });
      }
    },
    [update],
  );

  // Beim Verlassen der Seite mitten im Ziehen nichts zurücklassen.
  useEffect(
    () => () => {
      releaseRef.current?.();
      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current);
      }
    },
    [],
  );

  /** Eigenschaften für den Griff eines Eintrags. */
  function handleProps(id: string) {
    return {
      'data-sort-handle': '',
      style: { touchAction: 'none' as const },
      onPointerDown(event: PointerEvent<HTMLElement>) {
        if (event.pointerType === 'mouse' && event.button !== 0) {
          return;
        }
        event.preventDefault();
        const pointerId = event.pointerId;
        const copy: Record<string, string[]> = {};
        for (const [key, ids] of Object.entries(groups)) {
          copy[key] = [...ids];
        }
        startRef.current = copy;
        draggingRef.current = id;
        pointerRef.current = { x: event.clientX, y: event.clientY };
        update(copy);
        setDragging(id);
        if (frameRef.current === null) {
          frameRef.current = window.requestAnimationFrame(autoScroll);
        }
        // Die Ereignisse am Fenster abhören, nicht am Griff: Beim Umsortieren
        // verschiebt React den Eintrag im DOM, und der Griff verlöre dabei die
        // Zeigerbindung (pointer capture).
        const move = (moved: globalThis.PointerEvent) => {
          if (moved.pointerId === pointerId && draggingRef.current === id) {
            pointerRef.current = { x: moved.clientX, y: moved.clientY };
            hitTest();
          }
        };
        const up = (ended: globalThis.PointerEvent) => {
          if (ended.pointerId === pointerId && draggingRef.current === id) {
            finish(ended.type === 'pointerup');
          }
        };
        releaseRef.current?.();
        window.addEventListener('pointermove', move);
        window.addEventListener('pointerup', up);
        window.addEventListener('pointercancel', up);
        releaseRef.current = () => {
          window.removeEventListener('pointermove', move);
          window.removeEventListener('pointerup', up);
          window.removeEventListener('pointercancel', up);
        };
      },
      onKeyDown(event: KeyboardEvent<HTMLElement>) {
        if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') {
          return;
        }
        const at = locate(groups, id);
        if (at === null) {
          return;
        }
        const index = at.index + (event.key === 'ArrowUp' ? -1 : 1);
        if (index < 0 || index >= (groups[at.group]?.length ?? 0)) {
          return;
        }
        event.preventDefault();
        const next = moveInGroups(groups, id, at.group, index);
        onDropRef.current({ id, group: at.group, fromGroup: at.group, index, groups: next });
      },
    };
  }

  /** Eigenschaften für das Element eines Eintrags. */
  function itemProps(id: string, group: string) {
    return { 'data-sort-item': id, 'data-sort-group': group, 'data-sort-scope': scope };
  }

  /** Eigenschaften für den Rahmen einer Gruppe, damit man in leere Gruppen ablegen kann. */
  function containerProps(group: string) {
    return { 'data-sort-container': group, 'data-sort-scope': scope };
  }

  return {
    /** Reihenfolge zum Anzeigen: während des Ziehens die Vorschau. */
    groups: preview ?? groups,
    dragging,
    handleProps,
    itemProps,
    containerProps,
  };
}

/** Kurzform für eine einzelne Liste ohne Gruppen. */
export function useDragList(ids: readonly string[], onMove: (ids: string[]) => void) {
  const drag = useDragReorder({ all: ids }, (result) => onMove(result.groups.all ?? []));
  return {
    order: drag.groups.all ?? ids,
    dragging: drag.dragging,
    handleProps: drag.handleProps,
    itemProps: (id: string) => drag.itemProps(id, 'all'),
  };
}

/**
 * Ziehen und Ablegen für eine Liste von Objekten, zum Beispiel Zutaten oder
 * Schritte im Editor. Liefert die Einträge in Anzeigereihenfolge samt ihrer
 * Stelle in der ursprünglichen Liste (für Änderungen an einzelnen Feldern).
 */
export function useDragItems<T>(
  items: readonly T[],
  keyOf: (item: T, index: number) => string,
  onChange: (items: T[]) => void,
) {
  const keys = items.map(keyOf);
  const byKey = new Map(keys.map((key, index) => [key, { item: items[index] as T, index }]));
  const drag = useDragList(keys, (order) =>
    onChange(order.flatMap((key) => (byKey.has(key) ? [(byKey.get(key) as { item: T }).item] : []))),
  );
  return {
    entries: drag.order.flatMap((key) => {
      const entry = byKey.get(key);
      return entry === undefined ? [] : [{ key, ...entry }];
    }),
    dragging: drag.dragging,
    handleProps: drag.handleProps,
    itemProps: drag.itemProps,
  };
}
